import {
	Box,
	Button,
	FormControl,
	IconButton,
	TextareaAutosize,
	InputAdornment,
	TextField,
	Typography,
	useTheme,
	Select,
	MenuItem,
} from '@mui/material';
import React, {
	Dispatch,
	SetStateAction,
	useEffect,
	useRef,
	useState,
	useCallback,
} from 'react';
import CustomSwitch from '../../../../globalComponents/toggleButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

export interface ProposalConfig {
	signers: { name: string; email: string }[];
	signInPerson: boolean;
	notes?: string;
	paymentSchedule?: {
		percentage: string | number;
		description: string;
		type?: string;
	}[];
	secondSigner: boolean;
	includePaymentSchedule: boolean;
	includeCustomNotes: boolean;
}

interface SignerErrors {
	name?: string;
	email?: string;
}

const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const VALIDATION_MESSAGES = {
	NAME_REQUIRED: "Signer's full name is required",
	EMAIL_REQUIRED: 'Email address is required',
	EMAIL_INVALID: 'Please enter a valid email address',
} as const;

const ProposalConfig: React.FC<{
	quoteId: string | undefined;
	totalCost: number;
	configData: ProposalConfig;
	setConfigData: Dispatch<SetStateAction<ProposalConfig>>;
}> = ({ totalCost, configData, setConfigData }) => {
	const notesRef = useRef<HTMLTextAreaElement | null>();
	const theme = useTheme();
	const notesRequiredError =
		configData.includeCustomNotes &&
		(!configData.notes || !configData.notes.trim());
	const [signerErrors, setSignerErrors] = useState<SignerErrors[]>([]);
	const validateSigner = useCallback(
		(signer: { name: string; email: string }, index: number): SignerErrors => {
			const errors: SignerErrors = {};

			if (!signer.name.trim()) {
				errors.name = VALIDATION_MESSAGES.NAME_REQUIRED;
			}

			if (!signer.email.trim()) {
				errors.email = VALIDATION_MESSAGES.EMAIL_REQUIRED;
			} else if (!EMAIL_REGEX.test(signer.email)) {
				errors.email = VALIDATION_MESSAGES.EMAIL_INVALID;
			}
			setSignerErrors((prev) => {
				const updated = [...prev];
				updated[index] = errors;
				return updated;
			});

			return errors;
		},
		[]
	);
	const DisabledWrapper = React.useCallback(
		({ children, isDisabled }) => (
			<Box
				sx={{
					opacity: isDisabled ? 0.6 : 1,
					pointerEvents: isDisabled ? 'none' : 'auto',
				}}
			>
				{children}
			</Box>
		),
		[]
	);

	// Validate all signers
	const validateAllSigners = useCallback(() => {
		configData.signers.forEach((signer, index) => {
			validateSigner(signer, index);
		});
	}, [configData.signers, validateSigner]);

	useEffect(() => {
		if (configData.includeCustomNotes && notesRef.current) {
			notesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
			notesRef.current.focus();
		}
	}, [configData.includeCustomNotes, notesRef.current]);

	useEffect(() => {
		validateAllSigners();
	}, [configData.signers.length, validateAllSigners]);

	const handleSignerChange = useCallback(
		(e, index: number) => {
			const { name, value } = e.target;

			setConfigData((prev) => ({
				...prev,
				signers: prev.signers.map((signer, id) =>
					id == index ? { ...signer, [e.target.name]: e.target.value } : signer
				),
			}));

			const updatedSigner = {
				...configData.signers[index],
				[name]: value,
			};
			validateSigner(updatedSigner, index);
		},
		[configData.signers, setConfigData, validateSigner]
	);

	const totalPercentage = configData.paymentSchedule?.reduce((acc, current) => {
		if (current.type === '$') {
			// Convert amount to percentage
			return acc + (Number(current.percentage) / totalCost) * 100;
		}
		return acc + Number(current.percentage || 0);
	}, 0);

	// Round to 2 decimal places for comparison
	const roundedTotalPercentage = Math.round((totalPercentage || 0) * 100) / 100;

	const totalAmount = configData.paymentSchedule?.reduce((acc, current) => {
		if (current.type === '$') {
			return acc + Number(current.percentage || 0);
		}
		// Convert percentage to amount
		return acc + (Number(current.percentage) / 100) * totalCost;
	}, 0);

	// Check if schedule uses dollar amounts
	const usesAmountType = configData.paymentSchedule?.some(
		(s) => s.type === '$'
	);

	// Tolerance for amount validation (±$0.01)
	const AMOUNT_TOLERANCE = 0.01;
	const PERCENTAGE_TOLERANCE = 0.1;

	// Determine if validation error exists
	let paymentScheduleValidationError: string | null = null;

	if (configData.includePaymentSchedule && configData.paymentSchedule?.length) {
		if (usesAmountType) {
			// Validate dollar amounts against totalCost
			const roundedTotalAmount = Math.round((totalAmount || 0) * 100) / 100;
			const difference = Math.abs(roundedTotalAmount - totalCost);

			if (difference > AMOUNT_TOLERANCE) {
				if (roundedTotalAmount < totalCost) {
					const amountShort = totalCost - roundedTotalAmount;
					paymentScheduleValidationError = `Total payment schedule milestones should equal $${totalCost.toFixed(2)}. Currently, the schedule falls short by $${amountShort.toFixed(2)}.`;
				} else {
					const amountOver = roundedTotalAmount - totalCost;
					paymentScheduleValidationError = `Total payment schedule milestones should equal $${totalCost.toFixed(2)}. Currently, the schedule exceeds by $${amountOver.toFixed(2)}.`;
				}
			}
		} else {
			// Validate percentages equal 100%
			if (Math.abs(roundedTotalPercentage - 100) > PERCENTAGE_TOLERANCE) {
				if (roundedTotalPercentage < 100) {
					const percentageShort = 100 - roundedTotalPercentage;
					paymentScheduleValidationError = `Total payment schedule milestones should equal 100%. Currently, the schedule falls short by ${percentageShort.toFixed(2)}%.`;
				} else {
					const percentageOver = roundedTotalPercentage - 100;
					paymentScheduleValidationError = `Total payment schedule milestones should equal 100%. Currently, the schedule exceeds by ${percentageOver.toFixed(2)}%.`;
				}
			}
		}
	}

	return (
		<Box
			sx={{
				width: '37%',
				position: 'absolute',
				top: 100,
				right: 50,
				maxWidth: '400px',
				backgroundColor: theme.palette.background.paper,
				boxShadow: theme.shadows[2],
				borderRadius: theme.shape.borderRadius,
				padding: theme.spacing(2),
			}}
		>
			<Typography sx={{ color: theme.palette.grey[500], fontWeight: 600 }}>
				Signer Options
			</Typography>

			<FormControl fullWidth>
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: theme.typography.sm,
						marginTop: theme.spacing(2.5),
						marginBottom: theme.spacing(1.25),
					}}
				>
					1st Signer
				</Typography>

				<TextField
					fullWidth
					size="small"
					placeholder="Signer's Full Name"
					sx={{ marginBottom: theme.spacing(1.25) }}
					name="name"
					value={configData.signers[0].name || ''}
					onChange={(e) => handleSignerChange(e, 0)}
					error={!!signerErrors[0]?.name}
					helperText={signerErrors[0]?.name}
				/>

				<TextField
					fullWidth
					size="small"
					placeholder="Signer's Email"
					name="email"
					value={configData.signers[0]?.email || ''}
					onChange={(e) => handleSignerChange(e, 0)}
					error={!!signerErrors[0]?.email}
					helperText={signerErrors[0]?.email}
				/>

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: theme.spacing(1.875),
						marginBottom: theme.spacing(1.875),
					}}
				>
					<Typography sx={{ fontWeight: 600 }}>Include 2nd Signer</Typography>

					<CustomSwitch
						onChange={() => {
							setConfigData((prev) => {
								return {
									...prev,
									signers: prev.secondSigner
										? prev.signers.slice(0, 1)
										: [...prev.signers, { name: '', email: '' }],
									secondSigner: !prev.secondSigner,
								};
							});
						}}
						on={configData.secondSigner}
					/>
				</Box>

				{configData.secondSigner && (
					<>
						<TextField
							fullWidth
							size="small"
							placeholder="Signer's Full Name"
							sx={{ marginBottom: theme.spacing(1.25) }}
							name="name"
							value={configData.signers[1]?.name || ''}
							onChange={(e) => handleSignerChange(e, 1)}
							error={!!signerErrors[1]?.name}
							helperText={signerErrors[1]?.name}
						/>
						<TextField
							fullWidth
							size="small"
							placeholder="Signer's Email"
							name="email"
							value={configData.signers[1]?.email || ''}
							onChange={(e) => handleSignerChange(e, 1)}
							error={!!signerErrors[1]?.email}
							helperText={signerErrors[1]?.email}
						/>
					</>
				)}

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: theme.spacing(1.875),
						marginBottom: theme.spacing(1.875),
					}}
				>
					<Typography sx={{ fontWeight: 600 }}>Sign in Person</Typography>

					<CustomSwitch
						onChange={() => {
							setConfigData((prev) => ({
								...prev,
								signInPerson: !prev.signInPerson,
							}));
						}}
						on={configData.signInPerson}
					/>
				</Box>

				<Typography
					sx={{
						color: theme.palette.grey[500],
						fontWeight: 600,
						marginTop: theme.spacing(1.875),
					}}
				>
					Proposal Options
				</Typography>

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: theme.spacing(1.875),
						marginBottom: theme.spacing(1.875),
					}}
				>
					<Typography sx={{ fontWeight: 600 }}>
						Include Payment Schedule
					</Typography>

					<CustomSwitch
						onChange={() => {
							setConfigData((prev) => {
								return {
									...prev,
									paymentSchedule: prev.includePaymentSchedule
										? undefined
										: [{ percentage: 0, description: '' }],
									includePaymentSchedule: !prev.includePaymentSchedule,
								};
							});
						}}
						on={configData.includePaymentSchedule}
						disabled={true}
					/>
				</Box>

				{paymentScheduleValidationError &&
					configData.includePaymentSchedule && (
						<Typography
							sx={{
								color: theme.palette.error.main,
								fontSize: theme.typography.xs,
								marginBottom: theme.spacing(1.25),
							}}
						>
							{paymentScheduleValidationError}
						</Typography>
					)}

				{configData.includePaymentSchedule && (
					<Box>
						{configData.paymentSchedule?.map((_schedule, id) => (
							<Box
								key={id}
								sx={{
									marginBottom: theme.spacing(1.25),
								}}
							>
								<Box>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: theme.spacing(1),
										}}
									>
										<IconButton
											onClick={() => {
												setConfigData((prev) => ({
													...prev,
													paymentSchedule: prev.paymentSchedule?.filter(
														(_, index) => index !== id
													),
												}));
											}}
											sx={{ flexShrink: 0 }}
										>
											<CloseIcon />
										</IconButton>

										<TextField
											type="number"
											size="small"
											value={configData.paymentSchedule?.[id].percentage || ''}
											sx={{
												minWidth: _schedule.type === '$' ? '130px' : '90px',
												flexShrink: 0,
												display: {
													xs: 'none',
													sm: 'none',
													md: 'block',
												},
												maxWidth:
													_schedule.type === '$'
														? {
																md: '130px',
																lg: '150px',
															}
														: {
																md: '110px',
																lg: '130px',
															},
												transition: 'all 0.2s ease',
											}}
											inputProps={{
												step: '0.01',
												min: '0',
												max: _schedule.type === '$' ? undefined : '100',
											}}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end" sx={{ pr: 0 }}>
														<Select
															value={_schedule.type || '%'}
															onChange={(e) => {
																const newType = e.target.value;
																setConfigData((prev) => ({
																	...prev,
																	paymentSchedule: prev.paymentSchedule?.map(
																		(item) => ({
																			...item,
																			type: newType,
																		})
																	),
																}));
															}}
															variant="standard"
															disableUnderline
															displayEmpty
															renderValue={(selected) =>
																selected === '$' ? '$' : '%'
															}
															MenuProps={{
																sx: {
																	minWidth: '100px',
																	maxWidth: '150px',
																},
															}}
														>
															<MenuItem value="$">Amount $</MenuItem>
															<MenuItem value="%">Percent %</MenuItem>
														</Select>
													</InputAdornment>
												),
											}}
											onChange={(e) => {
												const value = e.target.value;

												setConfigData((prev) => ({
													...prev,
													paymentSchedule: prev.paymentSchedule?.map(
														(item, index) =>
															index === id
																? {
																		...item,
																		percentage: Number(value),
																	}
																: item
													),
												}));
											}}
										/>

										<TextField
											type="number"
											size="small"
											value={configData.paymentSchedule?.[id].percentage || ''}
											sx={{
												flex: 1,
												minWidth: _schedule.type === '$' ? '130px' : '90px',
												display: {
													xs: 'block',
													sm: 'block',
													md: 'none',
												},
												transition: 'all 0.2s ease',
											}}
											inputProps={{
												step: '0.01',
												min: '0',
												max: _schedule.type === '$' ? undefined : '100',
											}}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end" sx={{ pr: 0 }}>
														<Select
															value={_schedule.type || '%'}
															onChange={(e) => {
																const type = e.target.value;
																setConfigData((prev) => ({
																	...prev,
																	paymentSchedule: prev.paymentSchedule?.map(
																		(item, index) =>
																			index === id ? { ...item, type } : item
																	),
																}));
															}}
															variant="standard"
															disableUnderline
															displayEmpty
															renderValue={(selected) =>
																selected === '$' ? '$' : '%'
															}
															MenuProps={{
																sx: {
																	minWidth: '90px',
																	maxWidth: '140px',
																},
															}}
														>
															<MenuItem value="$">Amount $</MenuItem>
															<MenuItem value="%">Percent %</MenuItem>
														</Select>
													</InputAdornment>
												),
											}}
											onChange={(e) => {
												const value = e.target.value;

												setConfigData((prev) => ({
													...prev,
													paymentSchedule: prev.paymentSchedule?.map(
														(item, index) =>
															index === id
																? {
																		...item,
																		percentage: Number(value),
																	}
																: item
													),
												}));
											}}
										/>

										<TextField
											sx={{
												flex: 1,
												minWidth: 0,
												display: {
													xs: 'none',
													sm: 'none',
													md: 'block',
												},
											}}
											size="small"
											name="description"
											value={configData.paymentSchedule?.[id].description}
											error={
												!configData.paymentSchedule?.[id].description?.trim()
													?.length
											}
											onChange={(e) => {
												setConfigData((prev) => ({
													...prev,
													paymentSchedule: prev.paymentSchedule?.map(
														(item, index) =>
															index === id
																? { ...item, description: e.target.value }
																: item
													),
												}));
											}}
										/>
									</Box>

									<TextField
										sx={{
											width: '100%',
											marginTop: theme.spacing(1),
											display: {
												xs: 'block',
												sm: 'block',
												md: 'none',
											},
										}}
										size="small"
										name="description"
										value={configData.paymentSchedule?.[id].description}
										error={
											!configData.paymentSchedule?.[id].description?.trim()
												?.length
										}
										onChange={(e) => {
											setConfigData((prev) => ({
												...prev,
												paymentSchedule: prev.paymentSchedule?.map(
													(item, index) =>
														index === id
															? { ...item, description: e.target.value }
															: item
												),
											}));
										}}
									/>
								</Box>

								{_schedule.type === '$' ? (
									<Typography
										sx={{
											textAlign: 'end',
											// marginTop: theme.spacing(0.625),
											fontSize: theme.typography.sm,
											fontWeight: 600,
										}}
									>
										Percentage:
										<span style={{ marginLeft: theme.spacing(1.25) }}>
											{_schedule.percentage
												? `${((_schedule.percentage / totalCost) * 100).toFixed(2)}%`
												: '0%'}
										</span>
									</Typography>
								) : (
									<Typography
										sx={{
											textAlign: 'end',
											marginTop: theme.spacing(0.625),
											fontSize: theme.typography.sm,
											fontWeight: 600,
										}}
									>
										Amount:
										<span style={{ marginLeft: theme.spacing(1.25) }}>
											{_schedule.percentage
												? new Intl.NumberFormat('en-US', {
														style: 'currency',
														currency: 'USD',
													}).format((_schedule.percentage * totalCost) / 100)
												: '$0'}
										</span>
									</Typography>
								)}
							</Box>
						))}

						<Button
							fullWidth
							startIcon={<AddIcon />}
							sx={{
								marginTop: theme.spacing(1.875),
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.common.white,
								':hover': {
									backgroundColor: theme.palette.secondary.dark,
								},
							}}
							disabled={
								(configData.paymentSchedule &&
									configData.paymentSchedule?.length >= 7) ||
								roundedTotalPercentage >= 100 ||
								(totalAmount ?? 0) >= totalCost
							}
							onClick={() => {
								setConfigData((prev) => {
									// Get the type from the first milestone (if any)
									const existingType = prev.paymentSchedule?.[0]?.type || '%';

									return {
										...prev,
										paymentSchedule: prev.paymentSchedule
											? [
													...prev.paymentSchedule,
													{
														percentage: 0,
														description: '',
														type: existingType,
													},
												]
											: [{ percentage: 0, description: '', type: '%' }],
									};
								});
							}}
						>
							Add Payment Milestone
						</Button>
					</Box>
				)}

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: theme.spacing(1.875),
						marginBottom: theme.spacing(1.875),
					}}
				>
					<Typography sx={{ fontWeight: 600 }}>Include Custom Notes</Typography>

					<CustomSwitch
						onChange={() => {
							setConfigData((prev) => {
								return {
									...prev,
									notes: prev.includeCustomNotes ? undefined : '',
									includeCustomNotes: !prev.includeCustomNotes,
								};
							});
						}}
						on={configData.includeCustomNotes}
					/>
				</Box>

				{configData.includeCustomNotes && (
					<>
						{notesRequiredError && (
							<Typography
								sx={{
									color: theme.palette.error.main,
									fontSize: theme.typography.xs,
									marginBottom: theme.spacing(1.25),
								}}
							>
								Custom notes are required.
							</Typography>
						)}
						<TextareaAutosize
							ref={notesRef}
							minRows={3}
							style={{
								border: `1px solid ${theme.palette.grey[400]}`,
								borderRadius: theme.shape.borderRadius,
								padding: theme.spacing(0.625),
								outline: '0px',
								marginBottom: theme.spacing(3.125),
							}}
							placeholder="Capture additional notes and promises..."
							value={configData.notes}
							onChange={(e) => {
								setConfigData((prev) => ({
									...prev,
									notes: e.target.value,
								}));
							}}
						/>
					</>
				)}
			</FormControl>
		</Box>
	);
};

// Exported validation function for use in other components
export const validatePaymentSchedule = (
	configData: ProposalConfig,
	totalCost: number
): string | null => {
	const AMOUNT_TOLERANCE = 0.01;
	const PERCENTAGE_TOLERANCE = 0.1;

	if (
		!configData.includePaymentSchedule ||
		!configData.paymentSchedule?.length
	) {
		return null;
	}

	const usesAmountType = configData.paymentSchedule?.some(
		(s) => s.type === '$'
	);

	if (usesAmountType) {
		// Calculate total in dollar amounts
		const totalAmount = configData.paymentSchedule.reduce((acc, current) => {
			if (current.type === '$') {
				return acc + Number(current.percentage || 0);
			}
			// Convert percentage to amount
			return acc + (Number(current.percentage) / 100) * totalCost;
		}, 0);

		const roundedTotalAmount = Math.round(totalAmount * 100) / 100;
		const difference = Math.abs(roundedTotalAmount - totalCost);

		if (difference > AMOUNT_TOLERANCE) {
			if (roundedTotalAmount < totalCost) {
				const amountShort = totalCost - roundedTotalAmount;
				return `Total payment schedule milestones should equal $${totalCost.toFixed(2)}. Currently, the schedule falls short by $${amountShort.toFixed(2)}.`;
			} else {
				const amountOver = roundedTotalAmount - totalCost;
				return `Total payment schedule milestones should equal $${totalCost.toFixed(2)}. Currently, the schedule exceeds by $${amountOver.toFixed(2)}.`;
			}
		}
	} else {
		// Calculate total percentage
		const totalPercentage = configData.paymentSchedule.reduce(
			(acc, current) => {
				return acc + Number(current.percentage || 0);
			},
			0
		);

		const roundedTotalPercentage = Math.round(totalPercentage * 100) / 100;

		if (Math.abs(roundedTotalPercentage - 100) > PERCENTAGE_TOLERANCE) {
			if (roundedTotalPercentage < 100) {
				const percentageShort = 100 - roundedTotalPercentage;
				return `Total payment schedule milestones should equal 100%. Currently, the schedule falls short by ${percentageShort.toFixed(2)}%.`;
			} else {
				const percentageOver = roundedTotalPercentage - 100;
				return `Total payment schedule milestones should equal 100%. Currently, the schedule exceeds by ${percentageOver.toFixed(2)}%.`;
			}
		}
	}

	return null;
};

export default ProposalConfig;
